// Onglet « Facturation » d'un dossier, sur un seul écran : chiffres, provision,
// prestations, factures et devis, convention d'honoraires.
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FileSignature, Pencil, Plus, Trash2 } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Spinner } from '../ui/Spinner';
import { cn } from '../../lib/cn';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { useBillingDocuments } from '../../hooks/queries/useBillingDocuments';
import { useFeeAgreement, useUpsertFeeAgreement } from '../../hooks/queries/useFeeAgreement';
import { DossierTimeEntriesTab } from '../timeTracking/DossierTimeEntriesTab';
import { useTimeEntries } from '../../hooks/queries/useTimeEntries';
import { formatDuration } from '../../services/timeTracking/timeTrackingService';
import { useSetFeeAgreementProvision } from '../../hooks/queries/useFeeAgreement';
import { unbilledAmount } from './DossierOverviewBlocks';
import { toast } from 'sonner';
import {
  BILLING_DOC_TYPE_LABELS,
  computeFinancialSummary,
  formatEuros,
  type BillingDocument,
  type BillingDocumentStatus,
} from '../../services/billing/billingService';
import { polarDocumentNumber, polarStatusLabel } from '../../services/billing/polarDisplay';
import { NewPolarQuotePanel } from '../billing/NewPolarQuotePanel';
import { DossierExpensesSection } from '../billing/DossierExpensesSection';
import {
  FEE_AGREEMENT_MODE_LABELS,
  parseFeeServices,
  type FeeAgreement,
  type FeeAgreementMode,
  type FeeService,
} from '../../services/billing/feeAgreementService';

import { formatAppDate } from '../../lib/dateFormat';
import { FaCircleCheck, FaCircleExclamation, FaFileInvoice, FaHourglass, type AppIcon } from '../ui/FaIcons';
const STATUS_CLASSES: Record<BillingDocumentStatus, string> = {
  draft:    'bg-neutral-100 text-[#737373]',
  sent:     'bg-[rgba(201,168,76,0.14)] text-[#8a6d1f]',
  paid:     'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  overdue:  'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
  void:     'bg-neutral-100 text-neutral-400',
  accepted: 'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]',
  rejected: 'bg-[rgba(192,91,51,0.1)] text-[#c05b33]',
};

interface Props {
  dossierId: string;
  readOnly?: boolean;
}

export function DossierFacturationTab({ dossierId, readOnly = false }: Props) {
  const canEdit = useCanAccessFeature('fee_agreement_edit');
  const canCreate = useCanAccessFeature('billing_create') && !readOnly;
  const { data: agreement, isLoading: loadingAgreement } = useFeeAgreement(dossierId);
  const { data: docs } = useBillingDocuments({ dossier_id: dossierId });
  const { data: entries } = useTimeEntries({ dossier_id: dossierId });
  const [editing, setEditing] = useState(false);
  const [creating, setCreating] = useState<'invoice' | 'estimate' | null>(null);
  const [searchParams] = useSearchParams();
  // Ancien lien « ?section=convention » : la convention s'ouvre dépliée.
  const [conventionOpen, setConventionOpen] = useState(searchParams.get('section') === 'convention');

  const summary = useMemo(() => computeFinancialSummary(docs ?? []), [docs]);
  const unbilled = useMemo(() => {
    const list = (entries ?? []).filter((e) => e.is_billable && !e.invoiced_in_id);
    return {
      count: list.length,
      minutes: list.reduce((sum, e) => sum + e.duration_minutes, 0),
      amountHt: unbilledAmount(list),
    };
  }, [entries]);

  if (creating) {
    return (
      <div className="rounded-xl border border-neutral-200 bg-white p-5">
        <NewPolarQuotePanel dossierId={dossierId} docType={creating} onCancel={() => setCreating(null)} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
        <Figure
          accent
          icon={FaHourglass}
          label="Temps à facturer"
          value={formatEuros(unbilled.amountHt)}
          hint={`HT · ${formatDuration(unbilled.minutes)} sur ${unbilled.count} prestation${unbilled.count > 1 ? 's' : ''}`}
        />
        <Figure
          icon={FaFileInvoice}
          label="Facturé"
          value={formatEuros(summary.invoicedTtc)}
          hint={`TTC · ${summary.invoiceCount} facture${summary.invoiceCount > 1 ? 's' : ''}`}
        />
        <Figure icon={FaCircleCheck} label="Encaissé" value={formatEuros(summary.paidTtc)} hint="TTC" />
        <Figure
          icon={FaCircleExclamation}
          hot={summary.outstandingTtc > 0}
          label="Reste dû"
          value={formatEuros(summary.outstandingTtc)}
          hint={summary.overdueTtc > 0 ? `TTC · dont ${formatEuros(summary.overdueTtc)} en retard` : 'TTC'}
        />
      </div>

      <ProvisionGauge
        dossierId={dossierId}
        agreement={agreement ?? null}
        invoicedTtc={summary.invoicedTtc}
        unbilledHt={unbilled.amountHt}
        canEdit={canEdit && !readOnly}
      />

      <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_330px]">
        <section className="min-w-0 overflow-hidden rounded-xl border border-neutral-200 bg-white">
          <DossierTimeEntriesTab dossierId={dossierId} readOnly={readOnly} embedded />
        </section>

        <section className="rounded-xl border border-neutral-200 bg-white">
          <header className="flex min-h-[52px] items-center justify-between gap-2 border-b border-[#e6e2d8] px-4 py-2.5">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
              Factures et devis
              <span className="ml-2 font-normal tabular-nums text-neutral-400">{docs?.length ?? 0}</span>
            </h3>
            {canCreate ? (
              <span className="flex gap-1.5">
                <SmallButton onClick={() => setCreating('estimate')}>Devis</SmallButton>
                <SmallButton primary onClick={() => setCreating('invoice')}>Facture</SmallButton>
              </span>
            ) : null}
          </header>
          <InvoiceList docs={docs ?? []} />
        </section>
      </div>

      <DossierExpensesSection dossierId={dossierId} signedAt={agreement?.signed_at ?? null} readOnly={readOnly} />

      <section className="rounded-xl border border-neutral-200 bg-white">
        <header className="flex items-center justify-between gap-2 px-4 py-2.5">
          <button
            type="button"
            onClick={() => setConventionOpen((open) => !open)}
            aria-expanded={conventionOpen}
            className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted hover:text-cime-primary"
          >
            <svg viewBox="0 0 12 12" aria-hidden className={cn('h-3 w-3 transition-transform', !conventionOpen && '-rotate-90')}>
              <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Convention d'honoraires
            <span className="font-normal normal-case tracking-normal text-neutral-400">
              {agreement ? FEE_AGREEMENT_MODE_LABELS[agreement.billing_mode] : 'non renseignée'}
            </span>
          </button>
          {canEdit && agreement && !editing && conventionOpen ? (
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="w-4 h-4 mr-1.5" /> Modifier
            </Button>
          ) : null}
        </header>
        {conventionOpen ? (
          <div className="border-t border-[#e6e2d8] p-4">
            {loadingAgreement ? (
              <div className="flex items-center justify-center py-10"><Spinner /></div>
            ) : editing ? (
              <FeeAgreementForm dossierId={dossierId} agreement={agreement ?? null} onDone={() => setEditing(false)} />
            ) : agreement ? (
              <FeeAgreementSummary agreement={agreement} />
            ) : (
              <EmptyAgreement canEdit={canEdit} onCreate={() => setEditing(true)} />
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
}

function Figure({
  icon: Icon,
  label,
  value,
  hint,
  accent,
  hot,
}: {
  icon: AppIcon;
  label: string;
  value: string;
  hint: string;
  accent?: boolean;
  hot?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border border-neutral-200 bg-white px-4 py-3.5',
        accent && 'border-t-2 border-t-cime-gold',
      )}
    >
      <span
        className={cn(
          'grid h-8 w-8 flex-shrink-0 place-items-center rounded-lg',
          hot ? 'bg-[rgba(192,91,51,0.1)] text-[#c05b33]' : 'bg-[rgba(4,52,41,0.07)] text-cime-primary',
        )}
      >
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </span>
      <div className="min-w-0 leading-tight">
        <small className="block text-[11px] font-bold uppercase tracking-[0.08em] text-cime-text-muted">{label}</small>
        <b className={cn('mt-1 block text-[22px] font-semibold tabular-nums', hot ? 'text-[#c05b33]' : 'text-cime-text-primary')}>
          {value}
        </b>
        <span className="mt-0.5 block text-xs text-neutral-400">{hint}</span>
      </div>
    </div>
  );
}

function SmallButton({
  onClick,
  primary,
  children,
}: {
  onClick: () => void;
  primary?: boolean;
  children: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-7 items-center gap-1 rounded-full px-2.5 text-xs font-medium',
        primary
          ? 'bg-cime-primary text-white hover:opacity-90'
          : 'border border-neutral-200 bg-white text-cime-text-primary hover:border-neutral-300',
      )}
    >
      <Plus className="h-3 w-3" aria-hidden /> {children}
    </button>
  );
}

// ─── Provision ───────────────────────────────────────────────────────────────

/** Taux de TVA retenu pour estimer en TTC le temps pas encore facturé. */
const ESTIMATE_VAT = 1.2;

function ProvisionGauge({
  dossierId,
  agreement,
  invoicedTtc,
  unbilledHt,
  canEdit,
}: {
  dossierId: string;
  agreement: FeeAgreement | null;
  invoicedTtc: number;
  unbilledHt: number;
  canEdit: boolean;
}) {
  const save = useSetFeeAgreementProvision();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const provision = agreement?.provision_amount ?? null;

  function submit() {
    const text = draft.replace(/[\s\u00a0\u202f€]/g, '').replace(',', '.');
    if (text && !/^\d+(\.\d{1,2})?$/.test(text)) {
      toast.error('La provision doit être un montant positif, par exemple 2400.');
      return;
    }
    save.mutate(
      { dossierId, amount: text ? Number(text) : null },
      {
        onSuccess: () => setEditing(false),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer'),
      },
    );
  }

  if (editing) {
    return (
      <section className="flex flex-wrap items-center gap-2.5 rounded-xl border border-neutral-200 bg-white px-4 py-3">
        <label htmlFor="provision-amount" className="text-[13px] font-semibold text-cime-text-primary">
          Provision demandée (€ TTC)
        </label>
        <input
          id="provision-amount"
          autoFocus
          inputMode="decimal"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') setEditing(false);
          }}
          placeholder="Vide = pas de provision"
          className="h-9 w-48 rounded-lg border border-neutral-300 px-2.5 text-[13px] focus:border-cime-primary focus:outline-none"
        />
        <Button size="sm" onClick={submit} disabled={save.isPending}>Enregistrer</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>Annuler</Button>
      </section>
    );
  }

  const startEdit = () => {
    setDraft(provision == null ? '' : String(provision).replace('.', ','));
    setEditing(true);
  };

  if (provision == null) {
    if (!canEdit || !agreement) return null;
    return (
      <section className="flex items-center justify-between gap-3 rounded-xl border border-dashed border-neutral-300 bg-white px-4 py-2.5 text-[13px] text-cime-text-muted">
        <span>Aucune provision enregistrée pour ce dossier.</span>
        <button type="button" onClick={startEdit} className="font-semibold text-cime-primary hover:underline">
          Saisir la provision
        </button>
      </section>
    );
  }

  const unbilledTtc = unbilledHt * ESTIMATE_VAT;
  const used = invoicedTtc + unbilledTtc;
  const left = provision - used;
  const pct = (value: number) => (provision > 0 ? Math.min(100, Math.max(0, (value / provision) * 100)) : 0);
  const usedPct = provision > 0 ? Math.round((used / provision) * 100) : 0;

  return (
    <section className="rounded-xl border border-neutral-200 bg-white px-4 py-3.5">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
        <span className="text-cime-text-muted">
          <b className="font-semibold text-cime-text-primary">Provision de {formatEuros(provision)} TTC</b> · consommée à{' '}
          {usedPct} %
          {canEdit ? (
            <button type="button" onClick={startEdit} className="ml-2 text-xs font-semibold text-cime-primary hover:underline">
              Modifier
            </button>
          ) : null}
        </span>
        <span className={left < 0 ? 'font-semibold text-[#c8431f]' : 'text-cime-text-muted'}>
          {left < 0
            ? `dépassée de ${formatEuros(-left)} : appel de provision à prévoir`
            : `reste ${formatEuros(left)} avant appel de provision`}
        </span>
      </div>
      <div className="mt-2.5 flex h-2 overflow-hidden rounded-full bg-neutral-200">
        <i className="block h-full bg-cime-primary" style={{ width: `${pct(invoicedTtc)}%` }} />
        <i className="block h-full bg-cime-gold" style={{ width: `${pct(Math.min(unbilledTtc, Math.max(0, provision - invoicedTtc)))}%` }} />
      </div>
      <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs text-cime-text-muted">
        <Legend className="bg-cime-primary">Facturé {formatEuros(invoicedTtc)}</Legend>
        <Legend className="bg-cime-gold">Temps non facturé {formatEuros(unbilledTtc)} (estimé TTC)</Legend>
        <Legend className="bg-neutral-200">Disponible {formatEuros(Math.max(0, left))}</Legend>
      </div>
    </section>
  );
}

function Legend({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <i aria-hidden className={cn('h-2 w-2 rounded-full', className)} />
      {children}
    </span>
  );
}

// ─── Factures et devis du dossier ────────────────────────────────────────────

function InvoiceList({ docs }: { docs: BillingDocument[] }) {
  const navigate = useNavigate();
  if (docs.length === 0) {
    return <p className="px-4 py-6 text-center text-[13px] text-neutral-400">Aucune facture ni devis pour ce dossier.</p>;
  }
  return (
    <div>
      {docs.map((d) => {
        const overdue = d.status === 'overdue';
        return (
          <button
            key={d.id}
            type="button"
            onClick={() => navigate(`/facturation/${d.id}`)}
            className={cn(
              'flex w-full items-start justify-between gap-3 border-b border-l-2 border-neutral-100 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-neutral-50',
              overdue ? 'border-l-[#c05b33]' : 'border-l-transparent',
            )}
          >
            <span className="min-w-0 leading-tight">
              <b className="block truncate tabular-nums text-[13px] font-medium text-cime-text-primary">
                {polarDocumentNumber(d) ?? <span className="text-neutral-400">Brouillon</span>}
              </b>
              <small className="mt-0.5 block text-xs text-cime-text-muted">
                {BILLING_DOC_TYPE_LABELS[d.doc_type]}
                {d.issue_date ? ` · ${formatAppDate(d.issue_date)}` : ''}
              </small>
              {overdue && d.due_date ? (
                <small className="mt-1.5 flex items-center gap-1.5 text-xs text-[#c05b33]">
                  <FaCircleExclamation className="h-3 w-3 flex-shrink-0" aria-hidden />
                  Échéance dépassée depuis le {formatAppDate(d.due_date)}
                </small>
              ) : null}
            </span>
            <span className="flex flex-shrink-0 flex-col items-end gap-1">
              <b className="text-[15px] font-semibold tabular-nums text-cime-text-primary">{formatEuros(d.ttc_total)}</b>
              <span
                className={cn(
                  'inline-flex h-5 items-center rounded-full px-2 text-[11px] font-semibold',
                  STATUS_CLASSES[polarStatusLabel(d) === 'Figé' ? 'sent' : d.status],
                )}
              >
                {polarStatusLabel(d)}
              </span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Synthèse (lecture) ─────────────────────────────────────────────────────

function FeeAgreementSummary({ agreement }: { agreement: FeeAgreement }) {
  const services = parseFeeServices(agreement.services);
  const showForfait = agreement.billing_mode === 'forfait' || agreement.billing_mode === 'mixed';
  const showHourly = agreement.billing_mode === 'hourly' || agreement.billing_mode === 'mixed';
  const hasSuccessFee =
    agreement.success_fee_rate != null ||
    !!agreement.success_fee_basis ||
    !!agreement.success_fee_notes;

  return (
    <Card>
      <div className="space-y-4">
        {/* Mode + montants */}
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-cime-primary/10 text-cime-primary uppercase tracking-wide">
            {FEE_AGREEMENT_MODE_LABELS[agreement.billing_mode]}
          </span>
          {showForfait && agreement.forfait_amount != null && (
            <span className="text-sm text-neutral-700">
              Forfait : <strong className="text-neutral-900">{formatEuros(agreement.forfait_amount)}</strong>
            </span>
          )}
          {showHourly && agreement.hourly_rate != null && (
            <span className="text-sm text-neutral-700">
              Taux horaire : <strong className="text-neutral-900">{formatEuros(agreement.hourly_rate)}/h</strong>
            </span>
          )}
          {agreement.signed_at && (
            <span className="ml-auto text-xs text-neutral-500">
              Signée le {formatAppDate(agreement.signed_at)}
            </span>
          )}
        </div>

        {/* Prestations */}
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
            Prestations couvertes
          </p>
          {services.length > 0 ? (
            <ul className="list-disc list-inside space-y-0.5 text-sm text-neutral-700">
              {services.map((s, i) => (
                <li key={i}>{s.label}</li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neutral-400">Aucune prestation listée.</p>
          )}
        </div>

        {/* Honoraires de résultat */}
        {hasSuccessFee && (
          <div className="border-t border-neutral-100 pt-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
              Honoraires de résultat
            </p>
            <p className="text-sm text-neutral-700">
              {agreement.success_fee_rate != null && (
                <strong className="text-neutral-900">{agreement.success_fee_rate}&nbsp;%</strong>
              )}
              {agreement.success_fee_basis && (
                <span> {agreement.success_fee_basis}</span>
              )}
            </p>
            {agreement.success_fee_notes && (
              <p className="text-sm text-neutral-500 mt-1 whitespace-pre-wrap">{agreement.success_fee_notes}</p>
            )}
          </div>
        )}

        {/* Notes */}
        {agreement.notes && (
          <div className="border-t border-neutral-100 pt-3">
            <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">Notes</p>
            <p className="text-sm text-neutral-600 whitespace-pre-wrap">{agreement.notes}</p>
          </div>
        )}
      </div>
    </Card>
  );
}

function EmptyAgreement({ canEdit, onCreate }: { canEdit: boolean; onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 border border-dashed border-neutral-200 rounded-cime bg-white">
      <div className="w-12 h-12 rounded-2xl bg-cime-primary/10 flex items-center justify-center mb-3">
        <FileSignature className="w-6 h-6 text-cime-primary" />
      </div>
      <p className="text-sm text-cime-text-muted">Aucune convention d'honoraires enregistrée.</p>
      {canEdit && (
        <Button className="mt-3" onClick={onCreate}>
          <Plus className="w-4 h-4 mr-1.5" /> Ajouter une convention
        </Button>
      )}
    </div>
  );
}

// ─── Formulaire (édition) ───────────────────────────────────────────────────

function FeeAgreementForm({
  dossierId,
  agreement,
  onDone,
}: {
  dossierId: string;
  agreement: FeeAgreement | null;
  onDone: () => void;
}) {
  const upsert = useUpsertFeeAgreement();
  const [mode, setMode] = useState<FeeAgreementMode>(agreement?.billing_mode ?? 'forfait');
  const [forfait, setForfait] = useState(agreement?.forfait_amount?.toString() ?? '');
  const [hourly, setHourly] = useState(agreement?.hourly_rate?.toString() ?? '');
  const [services, setServices] = useState<FeeService[]>(
    agreement ? parseFeeServices(agreement.services) : [],
  );
  const [newService, setNewService] = useState('');
  const [successRate, setSuccessRate] = useState(agreement?.success_fee_rate?.toString() ?? '');
  const [successBasis, setSuccessBasis] = useState(agreement?.success_fee_basis ?? '');
  const [successNotes, setSuccessNotes] = useState(agreement?.success_fee_notes ?? '');
  const [signedAt, setSignedAt] = useState(agreement?.signed_at ?? '');
  const [notes, setNotes] = useState(agreement?.notes ?? '');

  const showForfait = mode === 'forfait' || mode === 'mixed';
  const showHourly = mode === 'hourly' || mode === 'mixed';

  function addService() {
    const label = newService.trim();
    if (!label) return;
    setServices((s) => [...s, { label }]);
    setNewService('');
  }

  function save() {
    upsert.mutate(
      {
        dossier_id: dossierId,
        billing_mode: mode,
        forfait_amount: showForfait && forfait ? Number(forfait) : null,
        hourly_rate: showHourly && hourly ? Number(hourly) : null,
        services,
        success_fee_rate: successRate ? Number(successRate) : null,
        success_fee_basis: successBasis.trim() || null,
        success_fee_notes: successNotes.trim() || null,
        signed_at: signedAt || null,
        notes: notes.trim() || null,
      },
      { onSuccess: () => onDone() },
    );
  }

  const MODES: FeeAgreementMode[] = ['forfait', 'hourly', 'mixed'];

  return (
    <Card>
      <div className="space-y-4">
        {/* Mode */}
        <Field label="Mode de facturation">
          <div className="grid grid-cols-3 gap-1.5 max-w-md">
            {MODES.map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={cn(
                  'px-2 py-1.5 text-xs rounded-md border transition',
                  mode === m
                    ? 'bg-cime-primary text-white border-cime-primary'
                    : 'bg-white border-neutral-200 hover:border-cime-primary/40',
                )}
              >
                {FEE_AGREEMENT_MODE_LABELS[m]}
              </button>
            ))}
          </div>
        </Field>

        {/* Montants */}
        <div className="grid grid-cols-2 gap-3">
          {showForfait && (
            <Field label="Montant du forfait (€)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={forfait}
                onChange={(e) => setForfait(e.target.value)}
                placeholder="3000"
              />
            </Field>
          )}
          {showHourly && (
            <Field label="Taux horaire (€/h)">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={hourly}
                onChange={(e) => setHourly(e.target.value)}
                placeholder="250"
              />
            </Field>
          )}
        </div>

        {/* Prestations */}
        <Field label="Prestations couvertes">
          <div className="space-y-1.5">
            {services.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md px-2.5 py-1.5">
                  {s.label}
                </span>
                <button
                  type="button"
                  onClick={() => setServices((arr) => arr.filter((_, j) => j !== i))}
                  className="text-neutral-400 hover:text-red-600"
                  aria-label="Retirer la prestation"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex items-center gap-2">
              <Input
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addService();
                  }
                }}
                placeholder="Rédaction de conclusions, audience…"
              />
              <Button type="button" variant="ghost" onClick={addService}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Field>

        {/* Honoraires de résultat */}
        <div className="border-t border-neutral-100 pt-4">
          <p className="text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-2">
            Honoraires de résultat
          </p>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Taux (%)">
              <Input
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={successRate}
                onChange={(e) => setSuccessRate(e.target.value)}
                placeholder="10"
              />
            </Field>
            <Field label="Base">
              <Input
                value={successBasis}
                onChange={(e) => setSuccessBasis(e.target.value)}
                placeholder="sur les sommes obtenues"
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Conditions (optionnel)">
              <textarea
                value={successNotes}
                onChange={(e) => setSuccessNotes(e.target.value)}
                rows={2}
                className="w-full text-sm rounded-cime border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cime-primary/30"
                placeholder="En cas de décision favorable, hors dépens…"
              />
            </Field>
          </div>
        </div>

        {/* Date + notes */}
        <div className="border-t border-neutral-100 pt-4 grid grid-cols-2 gap-3">
          <Field label="Date de signature">
            <Input type="date" value={signedAt} onChange={(e) => setSignedAt(e.target.value)} />
          </Field>
        </div>
        <Field label="Notes (optionnel)">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full text-sm rounded-cime border border-neutral-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cime-primary/30"
          />
        </Field>

        {upsert.isError && (
          <p className="text-sm text-red-600">
            {upsert.error instanceof Error ? upsert.error.message : 'Erreur lors de l\'enregistrement.'}
          </p>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onDone} disabled={upsert.isPending}>
            Annuler
          </Button>
          <Button onClick={save} disabled={upsert.isPending}>
            {upsert.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider font-semibold text-neutral-500 mb-1.5">
        {label}
      </label>
      {children}
    </div>
  );
}
